import React, { useState } from 'react';
import { auth, firestore } from '../firebase';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useCart } from '../contexts/CartContext';
import { saveCart } from '../services/firestoreService';

import styles from './CustomerRegister.module.css';

// ✅ Responsive background variants (exported by your image optimizer)
import bg480Avif  from '../images/register3-480.avif';
import bg768Avif  from '../images/register3-768.avif';
import bg1200Avif from '../images/register3-1200.avif';
import bg1920Avif from '../images/register3-1920.avif';

import bg480Webp  from '../images/register3-480.webp';
import bg768Webp  from '../images/register3-768.webp';
import bg1200Webp from '../images/register3-1200.webp';
import bg1920Webp from '../images/register3-1920.webp';

import bg480Jpg   from '../images/register3-480.jpg';
import bg768Jpg   from '../images/register3-768.jpg';
import bg1200Jpg  from '../images/register3-1200.jpg';
import bg1920Jpg  from '../images/register3-1920.jpg';

function BackgroundImage() {
  return (
    <div className={styles.bgWrap} aria-hidden="true">
      {/* Instant tiny blurred preview (bottom layer) */}
      <img src={bg480Jpg} alt="" className={styles.bgBlur} />

      {/* Sharp responsive image (top layer) */}
      <picture>
        <source
          type="image/avif"
          srcSet={[
            `${bg480Avif} 480w`,
            `${bg768Avif} 768w`,
            `${bg1200Avif} 1200w`,
            `${bg1920Avif} 1920w`,
          ].join(', ')}
          sizes="100vw"
        />
        <source
          type="image/webp"
          srcSet={[
            `${bg480Webp} 480w`,
            `${bg768Webp} 768w`,
            `${bg1200Webp} 1200w`,
            `${bg1920Webp} 1920w`,
          ].join(', ')}
          sizes="100vw"
        />
        <img
          className={styles.bgImg}
          alt=""
          src={bg1920Jpg}
          srcSet={[
            `${bg480Jpg} 480w`,
            `${bg768Jpg} 768w`,
            `${bg1200Jpg} 1200w`,
            `${bg1920Jpg} 1920w`,
          ].join(', ')}
          sizes="100vw"
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </picture>
    </div>
  );
}

const CustomerRegister = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    lastName: '',
    dob: '',
    dateRegistered: serverTimestamp(),
    ordersPlaced: 0,
    totalSpent: 0,
  });

  const navigate = useNavigate();
  const { setCart } = useCart();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Required fields
    if (!formData.name || !formData.lastName || !formData.email || !formData.password) {
      Swal.fire('Error', 'Todos los campos obligatorios deben ser completados.', 'error');
      return;
    }

    // Password rules
    if (formData.password.length < 8 || !/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      Swal.fire(
        'Error',
        'La contraseña debe tener al menos 8 caracteres, incluir una letra mayúscula y un número.',
        'error'
      );
      return;
    }

    // Match
    if (formData.password !== formData.confirmPassword) {
      Swal.fire('Error', 'Las contraseñas no coinciden.', 'error');
      return;
    }

    // DOB sanity
    if (formData.dob && new Date(formData.dob) > new Date()) {
      Swal.fire('Error', 'La fecha de nacimiento no puede ser en el futuro.', 'error');
      return;
    }

    try {
      const { email, password, name, lastName, dob } = formData;

      // Already registered?
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods.length > 0) {
        Swal.fire({
          title: 'Correo ya registrado',
          text: 'Este correo ya está registrado en nuestro sistema.',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Iniciar Sesión',
          cancelButtonText: 'Olvidé mi Contraseña',
        }).then((r) => {
          if (r.isConfirmed) navigate('/login');
          else if (r.dismiss === Swal.DismissReason.cancel) navigate('/reset-password');
        });
        return;
      }

      // Create user
      const { user } = await createUserWithEmailAndPassword(auth, email, password);

      // Save profile
      await setDoc(doc(firestore, 'users', user.uid), {
        email,
        name,
        lastName,
        dob: dob || null,
        cart: [],
        ordersPlaced: 0,
        totalSpent: 0,
        dateRegistered: new Date().toISOString(),
      });

      // Merge guest cart
      const guestCart = JSON.parse(localStorage.getItem('cart')) || [];
      await saveCart(guestCart);
      setCart(guestCart);
      localStorage.removeItem('cart');

      Swal.fire('Éxito', '¡Registro exitoso! Bienvenido.', 'success');
      navigate('/products');
    } catch (error) {
      console.error('Error registering user:', error);
      if (error.code === 'auth/email-already-in-use') {
        Swal.fire({
          title: 'Correo ya registrado',
          text: 'Este correo ya está registrado en nuestro sistema.',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Iniciar Sesión',
          cancelButtonText: 'Olvidé mi Contraseña',
        }).then((r) => {
          if (r.isConfirmed) navigate('/customer/login');
          else if (r.dismiss === Swal.DismissReason.cancel) navigate('/forgot-password');
        });
      } else {
        Swal.fire('Error', 'Hubo un problema al registrar el usuario. Inténtalo de nuevo.', 'error');
      }
    }
  };

  return (
    <div className={styles.container}>
      <BackgroundImage />

      <div className={styles.formWrapper}>
        <h2>Crear Cuenta</h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Nombre"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Apellidos"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Contraseña"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirmar Contraseña"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <p>Cumpleaños (Opcional)</p>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              placeholder="Fecha de Nacimiento (opcional)"
            />
          </div>
          <button type="submit" className={styles.btnPrimary}>Registrarse</button>
        </form>
      </div>
    </div>
  );
};

export default CustomerRegister;
